import {
    accountsApi,
    audienceInsightsApi,
    campaignGroupApi,
    campaignsApi,
    getMarketplaceProviders,
    performanceRulesApi,
    resourcesApi,
    sitesApi,
} from 'services/api';
import {
    byAccountDescriptionMapper,
    byCampaignsGroupMapper,
    byIdNameMapper,
    byNameUddIdMapper,
    byNameValueMapper,
    byRuleNameMapper,
    bySiteIdMapper,
    bySiteIdNameMapper,
    createPaginatedDataHandlerToOptions,
    creativeSizeMapper,
    dataHandlerByName,
    dataHandlerToOptions,
    dataHandlerToOSFamily,
    dataHandlerToOSVersion,
    defaultArgNames,
} from '../../components/Filters/filterUtils';
import { ARGUMENT_NAME } from './argumentName';
import { FILTER_TYPE } from './filterType';

export const FETCHED_VALUES_FILTER_MAP = {
    [FILTER_TYPE.COUNTRY]: {
        fetcher: async () => {
            const { results: countries } = await resourcesApi.getCountries();
            return countries
                .filter(country => !country.group)
                .map(country => ({ value: country.name, label: country.value }));
        },
        messagePrefix: 'app.country.code',
        responseTransformer: data => data.map(({ value }) => value),
    },
    [FILTER_TYPE.SITE]: {
        fetcher: sitesApi.getAllSites,
        argsNames: [ARGUMENT_NAME.ACCOUNT_ID, ...defaultArgNames],
        responseTransformer: createPaginatedDataHandlerToOptions(byAccountDescriptionMapper),
    },
    [FILTER_TYPE.SITE_ID]: {
        fetcher: sitesApi.getAllSites,
        argsNames: [ARGUMENT_NAME.ACCOUNT_ID, ...defaultArgNames],
        responseTransformer: createPaginatedDataHandlerToOptions(bySiteIdMapper),
    },
    [FILTER_TYPE.SITE_ID_VIEW]: {
        fetcher: sitesApi.getAllSites,
        argsNames: [ARGUMENT_NAME.ACCOUNT_ID, ...defaultArgNames],
        responseTransformer: createPaginatedDataHandlerToOptions(bySiteIdNameMapper),
    },
    [FILTER_TYPE.CATEGORY_TYPE]: {
        fetcher: resourcesApi.getCategories,
        messagePrefix: 'campaign.editor.category',
        responseTransformer: dataHandlerByName,
    },
    [FILTER_TYPE.IAB_CATEGORY_TYPE]: {
        fetcher: resourcesApi.getIabCategories,
        messagePrefix: 'campaign.editor.category.iab',
        responseTransformer: dataHandlerByName,
    },
    [FILTER_TYPE.LANGUAGE]: {
        fetcher: resourcesApi.getLanguages,
        messagePrefix: 'app.language.code',
        responseTransformer: dataHandlerByName,
    },
    [FILTER_TYPE.PARTNER_NAME]: {
        fetcher: getMarketplaceProviders,
        argsNames: [ARGUMENT_NAME.ACCOUNT_ID, ARGUMENT_NAME.QUERY_PARAMS, ...defaultArgNames],
        responseTransformer: ({ results }) => ({
            options: results.map(({ provider }) => ({ value: provider, label: provider })),
            hasMore: false,
        }),
    },
    [FILTER_TYPE.TARGET_PAGE_TYPE]: {
        fetcher: resourcesApi.getTargetPageType,
        responseTransformer: dataHandlerToOptions,
    },
    [FILTER_TYPE.ACCOUNT_MANAGER]: {
        fetcher: resourcesApi.getAccountManagers,
        argsNames: defaultArgNames,
        responseTransformer: createPaginatedDataHandlerToOptions(),
    },
    [FILTER_TYPE.ACCOUNT_MANAGER_GROUP]: {
        fetcher: resourcesApi.getAccountManagerGroups,
        argsNames: defaultArgNames,
        responseTransformer: createPaginatedDataHandlerToOptions(byNameValueMapper),
    },
    [FILTER_TYPE.SALES_GROUP]: {
        fetcher: resourcesApi.getSalesGroups,
        argsNames: defaultArgNames,
        responseTransformer: createPaginatedDataHandlerToOptions(),
    },
    [FILTER_TYPE.SALES_PERSON]: {
        fetcher: resourcesApi.getSalesPeople,
        argsNames: defaultArgNames,
        responseTransformer: createPaginatedDataHandlerToOptions(),
    },
    [FILTER_TYPE.BROWSER]: {
        fetcher: resourcesApi.getBrowsers,
        messagePrefix: 'campaign.editor.targeting.browser.options',
        argsNames: defaultArgNames,
        responseTransformer: dataHandlerByName,
    },
    [FILTER_TYPE.OS_FAMILY]: {
        fetcher: resourcesApi.getOsList,
        argsNames: defaultArgNames,
        responseTransformer: dataHandlerToOSFamily,
    },
    [FILTER_TYPE.OS_VERSION]: {
        fetcher: resourcesApi.getOsList,
        argsNames: defaultArgNames,
        responseTransformer: dataHandlerToOSVersion,
    },
    [FILTER_TYPE.CAMPAIGNS_GROUP]: {
        fetcher: (accountName, searchText, page, pageSize) =>
            campaignGroupApi.getCampaignGroupList(accountName, { searchText, page, pageSize }),
        argsNames: [ARGUMENT_NAME.ACCOUNT_ID, ...defaultArgNames],
        responseTransformer: createPaginatedDataHandlerToOptions(byCampaignsGroupMapper),
    },
    [FILTER_TYPE.CAMPAIGNS_GROUP_ID_VIEW]: {
        fetcher: (accountName, searchText, page, pageSize) =>
            campaignGroupApi.getCampaignGroupList(accountName, { searchText, page, pageSize }),
        argsNames: [ARGUMENT_NAME.ACCOUNT_ID, ...defaultArgNames],
        responseTransformer: createPaginatedDataHandlerToOptions(byIdNameMapper),
    },
    [FILTER_TYPE.BASELINE]: {
        fetcher: audienceInsightsApi.getBaselines,
        argsNames: [ARGUMENT_NAME.ACCOUNT_ID, ...defaultArgNames],
        responseTransformer: createPaginatedDataHandlerToOptions(byNameUddIdMapper),
    },
    [FILTER_TYPE.HISTORY_LOG_ACTIVITY_CODE_TYPE]: {
        messagePrefix: 'app.campaigns.reports.grid.campaign_history.activity_code',
        fetcher: resourcesApi.getHistoryLogActivityCodes,
        responseTransformer: dataHandlerByName,
    },
    [FILTER_TYPE.CONVERSION_TYPE]: {
        messagePrefix: 'report.conversionType',
        fetcher: resourcesApi.getConversionRuleTypes,
        responseTransformer: dataHandlerByName,
    },
    [FILTER_TYPE.CONVERSION_CATEGORY]: {
        messagePrefix: 'tracking.conversion.category',
        fetcher: resourcesApi.getConversionCategories,
        responseTransformer: dataHandlerByName,
    },
    [FILTER_TYPE.CONVERSION_STATUS]: {
        messagePrefix: 'report.conversionStatus',
        fetcher: resourcesApi.getConversionStatuses,
        responseTransformer: dataHandlerByName,
    },
    [FILTER_TYPE.ACCOUNT_NAME]: {
        argsNames: [ARGUMENT_NAME.ACCOUNT_ID, ARGUMENT_NAME.PAGE, ARGUMENT_NAME.PAGE_SIZE],
        fetcher: (...args) => {
            const [accountId, page, pageSize] = args.slice();
            return accountsApi.getAccountsUnderNetwork('', page, pageSize, accountId);
        },
        responseTransformer: createPaginatedDataHandlerToOptions(byAccountDescriptionMapper),
    },
    [FILTER_TYPE.CAMPAIGN_ID]: {
        fetcher: (accountName, searchText, page, pageSize) =>
            campaignsApi.getCampaigns({
                accountId: accountName,
                page,
                pageSize,
                searchText,
            }),
        argsNames: [ARGUMENT_NAME.ACCOUNT_ID, ...defaultArgNames],
        responseTransformer: createPaginatedDataHandlerToOptions(byIdNameMapper),
    },
    [FILTER_TYPE.RULE_NAME]: {
        fetcher: (accountName, searchText, page, pageSize) =>
            performanceRulesApi.getAccountRules(accountName, page, pageSize, searchText),
        argsNames: [ARGUMENT_NAME.ACCOUNT_ID, ...defaultArgNames],
        responseTransformer: createPaginatedDataHandlerToOptions(byRuleNameMapper),
    },
    [FILTER_TYPE.SIZE]: {
        fetcher: resourcesApi.getAdDimensions,
        argsNames: defaultArgNames,
        responseTransformer: createPaginatedDataHandlerToOptions(creativeSizeMapper),
    },
    [FILTER_TYPE.CREATIVE_LIBRARY_SIZE]: {
        fetcher: resourcesApi.getAdDimensions,
        argsNames: defaultArgNames,
        responseTransformer: createPaginatedDataHandlerToOptions(creativeSizeMapper),
    },
};
